import React, { useEffect, useRef, useState } from 'react';
import { getGameNotification, GameNotificationType } from '../gameUtils';
import { subscribeToScoreChanges } from '../scoreEvents';

export const GAME_NOTIFICATION_DURATION_MS = 4000;

function notificationClassName(notification) {
    const classes = ['game-notification-card'];

    if (notification.team) {
        classes.push(notification.team);
    } else {
        classes.push('neutral');
    }

    return classes.join(' ');
}

function notificationSignature(notification) {
    return [
        notification.type,
        notification.team ?? 'neutral',
        notification.title,
        notification.message
    ].join('|');
}

export default function GameNotificationToast({ enabled, settings }) {
    const [notification, setNotification] = useState(null);
    const timeoutRef = useRef(null);
    const lastNotificationSignatureRef = useRef(null);

    useEffect(() => {
        return subscribeToScoreChanges(({ redScore, blueScore }) => {
            if (!enabled) return;

            const nextNotification = getGameNotification(redScore, blueScore, settings);
            if (nextNotification.type === GameNotificationType.NONE) {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                    timeoutRef.current = null;
                }
                setNotification(null);
                if (redScore === 0 && blueScore === 0) {
                    lastNotificationSignatureRef.current = null;
                }
                return;
            }

            const nextNotificationSignature = notificationSignature(nextNotification);
            if (nextNotificationSignature === lastNotificationSignatureRef.current) {
                return;
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            lastNotificationSignatureRef.current = nextNotificationSignature;
            setNotification({
                ...nextNotification,
                key: `${nextNotification.type}-${nextNotification.team ?? 'neutral'}-${redScore}-${blueScore}-${Date.now()}`
            });
            timeoutRef.current = setTimeout(() => {
                setNotification(null);
                timeoutRef.current = null;
            }, GAME_NOTIFICATION_DURATION_MS);
        });
    }, [enabled, settings]);

    useEffect(() => {
        if (enabled) return undefined;

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setNotification(null);

        return undefined;
    }, [enabled]);

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    if (!notification) {
        return null;
    }

    return (
        <div
            key={notification.key}
            className="notification-toast game-notification-toast"
            role="status"
            aria-live="polite"
            style={{ '--game-notification-duration': `${GAME_NOTIFICATION_DURATION_MS}ms` }}
        >
            <div className={`notification-card ${notificationClassName(notification)}`}>
                <strong>{notification.title}</strong>
                <span>{notification.message}</span>
            </div>
        </div>
    );
}
